import { PRESET_SCHEMA_VERSION } from "./capabilities.js";
import { createEmptyPreset, defaultModulator, } from "./model.js";
import { LFO_SHAPES, MOD_DESTINATIONS, MOD_SOURCES, PARAM_NAMES, PARAM_RANGES, PRESET_STYLES, WAVETABLE_GENERATORS, } from "./params.js";
function isRecord(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
function asNumber(value, fallback) {
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function migrateParams(raw) {
    const out = {};
    const src = isRecord(raw) ? raw : {};
    for (const key of PARAM_NAMES) {
        const range = PARAM_RANGES[key];
        const value = asNumber(src[key], range.default);
        out[key] = Math.min(range.max, Math.max(range.min, value));
    }
    return out;
}
function migrateLfoShapes(raw) {
    const defaults = {
        1: "sine",
        2: "triangle",
        3: "square",
        4: "saw",
        5: "sine",
        6: "sine",
        7: "sine",
        8: "sine",
    };
    if (!isRecord(raw))
        return defaults;
    const out = { ...defaults };
    for (let i = 1; i <= 8; i++) {
        const value = raw[String(i)] ?? raw[i];
        if (typeof value === "string" && LFO_SHAPES.includes(value)) {
            out[i] = value;
        }
    }
    return out;
}
function migrateWavetable(raw, fallback) {
    if (!isRecord(raw) || typeof raw.kind !== "string")
        return fallback;
    if (raw.kind === "basic" &&
        typeof raw.shape === "string" &&
        WAVETABLE_GENERATORS.includes(raw.shape)) {
        return { kind: "basic", shape: raw.shape };
    }
    if (raw.kind === "pulse" && typeof raw.width === "number") {
        return {
            kind: "pulse",
            width: Math.min(0.95, Math.max(0.05, raw.width)),
        };
    }
    if (raw.kind === "harmonic" && Array.isArray(raw.harmonics)) {
        const harmonics = raw.harmonics
            .filter((h) => typeof h === "number" && Number.isFinite(h))
            .map((h) => Math.min(1, Math.max(0, h)))
            .slice(0, 64);
        if (harmonics.length > 0)
            return { kind: "harmonic", harmonics };
    }
    return fallback;
}
function migrateModulations(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const used = new Set();
    for (const entry of raw) {
        if (!isRecord(entry))
            continue;
        const slot = Math.round(asNumber(entry.slot, 0));
        if (slot < 1 || slot > 64 || used.has(slot))
            continue;
        const source = entry.source;
        const destination = entry.destination;
        if (typeof source !== "string" || !MOD_SOURCES.includes(source)) {
            continue;
        }
        if (typeof destination !== "string" ||
            !MOD_DESTINATIONS.includes(destination)) {
            continue;
        }
        used.add(slot);
        out.push({
            slot,
            source: source,
            destination: destination,
            amount: Math.min(1, Math.max(-1, asNumber(entry.amount, 0))),
            bipolar: Boolean(entry.bipolar),
            ...(typeof entry.ownedBySequence === "number"
                ? { ownedBySequence: Math.round(entry.ownedBySequence) }
                : {}),
        });
    }
    return out.sort((a, b) => a.slot - b.slot);
}
function migrateModulators(raw, lfoShapes) {
    const byIndex = new Map();
    if (Array.isArray(raw)) {
        for (const entry of raw) {
            if (!isRecord(entry))
                continue;
            const index = Math.round(asNumber(entry.index, 0));
            if (index < 1 || index > 8)
                continue;
            const base = defaultModulator(index, lfoShapes[index] ?? "sine");
            const shape = isRecord(entry.shape) ? entry.shape : null;
            let nextShape = base.shape;
            if (shape?.kind === "preset" && typeof shape.shape === "string") {
                if (LFO_SHAPES.includes(shape.shape)) {
                    nextShape = { kind: "preset", shape: shape.shape };
                }
            }
            else if (shape?.kind === "custom" && Array.isArray(shape.points)) {
                const points = shape.points
                    .filter(isRecord)
                    .map((pt) => ({
                    x: Math.min(1, Math.max(0, asNumber(pt.x, 0))),
                    y: Math.min(1, Math.max(0, asNumber(pt.y, 0))),
                    ...(typeof pt.power === "number" ? { power: pt.power } : {}),
                }))
                    .slice(0, 64);
                if (points.length >= 2) {
                    nextShape = { kind: "custom", points, smooth: Boolean(shape.smooth) };
                }
            }
            else if (shape?.kind === "steps" && Array.isArray(shape.values)) {
                const values = shape.values
                    .filter((v) => typeof v === "number" && Number.isFinite(v))
                    .map((v) => Math.min(1, Math.max(0, v)))
                    .slice(0, 32);
                if (values.length > 0) {
                    nextShape = {
                        kind: "steps",
                        values,
                        ...(typeof shape.smoothing === "number" ? { smoothing: shape.smoothing } : {}),
                    };
                }
            }
            const timing = isRecord(entry.timing) ? entry.timing : null;
            let nextTiming = base.timing;
            if (timing?.mode === "tempo" && typeof timing.division === "string") {
                nextTiming = { mode: "tempo", division: timing.division };
            }
            else if (timing?.mode === "free_hz") {
                nextTiming = {
                    mode: "free_hz",
                    frequencyHz: Math.min(20, Math.max(0.01, asNumber(timing.frequencyHz, 1))),
                };
            }
            byIndex.set(index, {
                index,
                shape: nextShape,
                timing: nextTiming,
                trigger: entry.trigger === "sync" ||
                    entry.trigger === "free" ||
                    entry.trigger === "one_shot" ||
                    entry.trigger === "note_retrigger"
                    ? entry.trigger
                    : "note_retrigger",
                phase: Math.min(1, Math.max(0, asNumber(entry.phase, 0))),
                fadeSeconds: Math.min(8, Math.max(0, asNumber(entry.fadeSeconds, 0))),
                delaySeconds: Math.min(4, Math.max(0, asNumber(entry.delaySeconds, 0))),
                stereoOffset: Math.min(0.5, Math.max(-0.5, asNumber(entry.stereoOffset, 0))),
                ...(typeof entry.ownedBySequence === "number"
                    ? { ownedBySequence: Math.round(entry.ownedBySequence) }
                    : {}),
            });
        }
    }
    return [1, 2, 3, 4, 5, 6, 7, 8].map((index) => {
        const existing = byIndex.get(index);
        if (existing)
            return existing;
        return defaultModulator(index, lfoShapes[index] ?? "sine");
    });
}
function migrateMacros(raw) {
    const defaults = createEmptyPreset().macros;
    if (!Array.isArray(raw))
        return defaults;
    const byIndex = new Map();
    for (const entry of raw) {
        if (!isRecord(entry))
            continue;
        const index = Math.round(asNumber(entry.index, 0));
        if (index < 1 || index > 4)
            continue;
        const name = typeof entry.name === "string" && entry.name.trim().length > 0
            ? entry.name.trim().slice(0, 64)
            : `MACRO ${index}`;
        const assignments = Array.isArray(entry.assignments)
            ? entry.assignments
                .filter(isRecord)
                .filter((a) => typeof a.destination === "string" &&
                MOD_DESTINATIONS.includes(a.destination))
                .map((a) => ({
                destination: a.destination,
                amount: Math.min(1, Math.max(-1, asNumber(a.amount, 0))),
                ...(a.bipolar ? { bipolar: true } : {}),
            }))
                .slice(0, 8)
            : [];
        byIndex.set(index, { index, name, assignments });
    }
    return defaults.map((d) => byIndex.get(d.index) ?? d);
}
function migrateSequences(raw) {
    if (!Array.isArray(raw))
        return [];
    // Full sequence validation happens in normalize; migration preserves well-formed data.
    return raw.filter(isRecord).map((entry, i) => {
        const index = Math.round(asNumber(entry.index, i + 1));
        return entry;
    }).filter((s) => s && typeof s.index === "number");
}
/**
 * Migrate any persisted/legacy preset document to the current schemaVersion.
 * Preserves name, style, comments, parameters, modulation slots, LFO shapes,
 * and generated wavetables. New fields receive neutral defaults.
 */
export function migratePresetModel(raw) {
    const empty = createEmptyPreset();
    if (!isRecord(raw))
        return empty;
    const style = typeof raw.style === "string" && PRESET_STYLES.includes(raw.style)
        ? raw.style
        : "other";
    const lfoShapes = migrateLfoShapes(raw.lfoShapes);
    const wavetablesRaw = isRecord(raw.wavetables) ? raw.wavetables : {};
    const model = {
        schemaVersion: PRESET_SCHEMA_VERSION,
        name: typeof raw.name === "string" && raw.name.trim() ? raw.name.trim().slice(0, 64) : "Init",
        style,
        comments: typeof raw.comments === "string" ? raw.comments.slice(0, 500) : "",
        params: migrateParams(raw.params),
        modulations: migrateModulations(raw.modulations),
        lfoShapes,
        modulators: migrateModulators(raw.modulators, lfoShapes),
        sequences: migrateSequences(raw.sequences),
        macros: migrateMacros(raw.macros),
        wavetables: {
            1: migrateWavetable(wavetablesRaw[1] ?? wavetablesRaw["1"], empty.wavetables[1]),
            2: migrateWavetable(wavetablesRaw[2] ?? wavetablesRaw["2"], empty.wavetables[2]),
            3: migrateWavetable(wavetablesRaw[3] ?? wavetablesRaw["3"], empty.wavetables[3]),
        },
    };
    // Older generated sequences gated Vital's global output gain. Re-route only
    // sequence-owned gates so existing user-authored volume modulation remains
    // untouched while regenerated/exported presets follow the safer topology.
    for (const sequence of model.sequences) {
        if (sequence.allocation?.gateDestination === "volume") {
            sequence.allocation.gateDestination = "osc_1_level";
        }
    }
    for (const modulation of model.modulations) {
        if (modulation.ownedBySequence != null && modulation.destination === "volume") {
            modulation.destination = "osc_1_level";
        }
    }
    // v1 docs only had lfo*_frequency params — push those into default modulators.
    // v2 docs with modulators are authoritative; mirror free-Hz timing back onto params.
    const hadModulators = Array.isArray(raw.modulators) && raw.modulators.length > 0;
    for (const mod of model.modulators) {
        const key = `lfo${mod.index}_frequency`;
        if (key in model.params && mod.timing.mode === "free_hz") {
            if (hadModulators) {
                model.params[key] = mod.timing.frequencyHz;
            }
            else {
                mod.timing = { mode: "free_hz", frequencyHz: model.params[key] };
            }
        }
        if (mod.shape.kind === "preset") {
            model.lfoShapes[mod.index] = mod.shape.shape;
        }
    }
    return model;
}
/** True when a persisted document looks like a pre-v2 (implicit v1) model. */
export function isLegacyPresetModel(raw) {
    if (!isRecord(raw))
        return false;
    return raw.schemaVersion == null || raw.schemaVersion === 1;
}
//# sourceMappingURL=migrate.js.map